import React, { useMemo, useState } from 'react';
import DetailedAnalytics from './DetailedAnalytics';
import { businessDaysUntil } from '../utils/businessDays';
import { apiFetch } from '../utils/api';
import { useSalesMembers } from '../hooks/useSalesMembers';
import { PageHeader, Button, KpiCard, Card, CardHeader } from './ui';

// 決定実績データ（月別） - APIから取得予定
const DECISIONS_BY_MONTH = {};

// 決定目標バナーコンポーネント
const MonthlyGoalBanner = ({ ppGoal, bpGoal, ppAchieved, bpAchieved, selectedMonth, onMonthChange, ppPrevMonth, bpPrevMonth, onPpGoalChange, onBpGoalChange }) => {
  const ppProgress = ppGoal > 0 ? (ppAchieved / ppGoal) * 100 : 0;
  const bpProgress = bpGoal > 0 ? (bpAchieved / bpGoal) * 100 : 0;
  const totalRemaining = (ppGoal - ppAchieved) + (bpGoal - bpAchieved);

  // PP目標インライン編集
  const [editingPp, setEditingPp] = React.useState(false);
  const [ppInputVal, setPpInputVal] = React.useState('');
  const ppInputRef = React.useRef(null);

  // BP目標インライン編集
  const [editingBp, setEditingBp] = React.useState(false);
  const [bpInputVal, setBpInputVal] = React.useState('');
  const bpInputRef = React.useRef(null);

  const startEditPp = () => {
    setPpInputVal(String(ppGoal));
    setEditingPp(true);
    setTimeout(() => ppInputRef.current?.select(), 0);
  };
  const commitPp = () => {
    onPpGoalChange && onPpGoalChange(ppInputVal);
    setEditingPp(false);
  };

  const startEditBp = () => {
    setBpInputVal(String(bpGoal));
    setEditingBp(true);
    setTimeout(() => bpInputRef.current?.select(), 0);
  };
  const commitBp = () => {
    onBpGoalChange && onBpGoalChange(bpInputVal);
    setEditingBp(false);
  };

  // 残り日数を実際の月末から計算
  const now = new Date();
  const currentYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const isCurrentMonth = selectedMonth === currentYM;
  const [selYear, selMonthNum] = selectedMonth.split('-').map(Number);
  const lastDay = new Date(selYear, selMonthNum, 0).getDate();
  const daysLeft = isCurrentMonth ? lastDay - now.getDate() : 0;

  const ppChange = ppAchieved - ppPrevMonth;
  const bpChange = bpAchieved - bpPrevMonth;

  const getChangeIcon = (change) => {
    if (change > 0) return { icon: 'fa-arrow-up', color: 'text-green-400' };
    if (change < 0) return { icon: 'fa-arrow-down', color: 'text-red-400' };
    return { icon: 'fa-minus', color: 'text-gray-400' };
  };

  // 過去3ヶ月分を動的生成
  const months = Array.from({ length: 3 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${d.getFullYear()}年${d.getMonth() + 1}月${i === 0 ? '（今月）' : ''}`;
    return { value: val, label };
  });

  return (
    <Card className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold text-prodia-text-primary flex items-center gap-2">
            <i className="fas fa-bullseye text-prodia-primary"></i>
            決定目標
          </h2>
          <select
            value={selectedMonth}
            onChange={(e) => onMonthChange(e.target.value)}
            className="border border-prodia-border bg-prodia-surface text-prodia-text-primary rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-prodia-background transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-prodia-primary/30"
          >
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="text-prodia-text-secondary text-sm">
          {isCurrentMonth
            ? <>残り<span className="text-xl font-bold text-prodia-text-primary mx-1">{daysLeft}</span>日</>
            : <span className="text-prodia-text-muted text-sm">過去月</span>
          }
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* PP目標 */}
        <div className="bg-prodia-background border border-prodia-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-prodia-text-secondary text-sm font-medium">PP営業</span>
            <div className="flex items-center gap-2">
              <span className="text-prodia-text-primary font-bold text-lg">
                {ppAchieved}/
                {editingPp ? (
                  <input
                    ref={ppInputRef}
                    type="number"
                    min="0"
                    value={ppInputVal}
                    onChange={(e) => setPpInputVal(e.target.value)}
                    onBlur={commitPp}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitPp(); if (e.key === 'Escape') setEditingPp(false); }}
                    className="w-12 text-center bg-prodia-surface border border-prodia-border rounded-lg text-prodia-text-primary font-bold text-base px-1 focus:outline-none focus:ring-2 focus:ring-prodia-primary/30"
                  />
                ) : (
                  <button
                    title="クリックして目標を変更"
                    onClick={startEditPp}
                    className="underline decoration-dotted hover:text-prodia-primary transition-colors cursor-pointer"
                  >{ppGoal}</button>
                )}
                名
              </span>
              {ppPrevMonth !== undefined && (
                <span className={`flex items-center gap-1 text-xs ${getChangeIcon(ppChange).color}`}>
                  <i className={`fas ${getChangeIcon(ppChange).icon}`}></i>
                  {ppChange !== 0 && Math.abs(ppChange)}
                </span>
              )}
            </div>
          </div>
          <div className="h-3 bg-prodia-border/40 rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-prodia-primary transition-all duration-500"
              style={{ width: `${Math.min(ppProgress, 100)}%` }}
            ></div>
          </div>
          <div className="text-prodia-text-muted text-xs flex items-center justify-between">
            <span>
              残り<span className="text-prodia-text-primary font-semibold mx-1">{ppGoal - ppAchieved}名</span>
              ({ppProgress.toFixed(0)}%達成)
            </span>
            {ppPrevMonth !== undefined && (
              <span className="text-prodia-text-muted">先月: {ppPrevMonth}名</span>
            )}
          </div>
        </div>

        {/* BP目標 */}
        <div className="bg-prodia-background border border-prodia-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-prodia-text-secondary text-sm font-medium">BP営業</span>
            <div className="flex items-center gap-2">
              <span className="text-prodia-text-primary font-bold text-lg">
                {bpAchieved}/
                {editingBp ? (
                  <input
                    ref={bpInputRef}
                    type="number"
                    min="0"
                    value={bpInputVal}
                    onChange={(e) => setBpInputVal(e.target.value)}
                    onBlur={commitBp}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitBp(); if (e.key === 'Escape') setEditingBp(false); }}
                    className="w-12 text-center bg-prodia-surface border border-prodia-border rounded-lg text-prodia-text-primary font-bold text-base px-1 focus:outline-none focus:ring-2 focus:ring-prodia-primary/30"
                  />
                ) : (
                  <button
                    title="クリックして目標を変更"
                    onClick={startEditBp}
                    className="underline decoration-dotted hover:text-prodia-primary transition-colors cursor-pointer"
                  >{bpGoal}</button>
                )}
                名
              </span>
              {bpPrevMonth !== undefined && (
                <span className={`flex items-center gap-1 text-xs ${getChangeIcon(bpChange).color}`}>
                  <i className={`fas ${getChangeIcon(bpChange).icon}`}></i>
                  {bpChange !== 0 && Math.abs(bpChange)}
                </span>
              )}
            </div>
          </div>
          <div className="h-3 bg-prodia-border/40 rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-success transition-all duration-500"
              style={{ width: `${Math.min(bpProgress, 100)}%` }}
            ></div>
          </div>
          <div className="text-prodia-text-muted text-xs flex items-center justify-between">
            <span>
              残り<span className="text-prodia-text-primary font-semibold mx-1">{bpGoal - bpAchieved}名</span>
              ({bpProgress.toFixed(0)}%達成)
            </span>
            {bpPrevMonth !== undefined && (
              <span className="text-prodia-text-muted">先月: {bpPrevMonth}名</span>
            )}
          </div>
        </div>
      </div>

      {totalRemaining > 0 && isCurrentMonth && (
        <div className="mt-4 pt-4 border-t border-prodia-border">
          <p className="text-prodia-text-secondary text-sm flex items-center gap-2">
            <i className="fas fa-fire text-warning"></i>
            目標達成まで<span className="text-warning font-bold text-lg mx-1">{totalRemaining}件</span>必要！
          </p>
        </div>
      )}
    </Card>
  );
};

// 今月のアクションカード
const MonthlyActionsCard = ({ engineers }) => {
  const now = new Date();
  const thisYear = now.getFullYear();
  const thisMonth = now.getMonth(); // 0-indexed

  // 今月中に契約終了するアサイン済エンジニア
  const endingThisMonth = engineers.filter(e => {
    if (e.engineer_status !== 'アサイン済' || !e.project_end_date) return false;
    const end = new Date(e.project_end_date);
    return end.getFullYear() === thisYear && end.getMonth() === thisMonth;
  }).sort((a, b) => new Date(a.project_end_date) - new Date(b.project_end_date));

  const monthLabel = `${thisYear}年${thisMonth + 1}月`;

  return (
    <Card className="flex flex-col h-full">
      <h3 className="text-lg font-semibold text-prodia-text-primary mb-1 flex items-center gap-2">
        <i className="fas fa-calendar-alt text-prodia-primary"></i>
        今月のアクション
      </h3>
      <p className="text-xs text-prodia-text-muted mb-4">{monthLabel}中に次のアサイン先を決める必要があるエンジニア</p>

      {endingThisMonth.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-6 text-slate-400">
          <i className="fas fa-check-circle text-3xl mb-2 text-emerald-400"></i>
          <p className="text-sm">今月中に契約終了のエンジニアはいません</p>
        </div>
      ) : (
        <div className="space-y-2 flex-1">
          {endingThisMonth.map(e => {
            const daysLeft = businessDaysUntil(e.project_end_date);
            const isUrgent = daysLeft <= 7;
            const isVeryUrgent = daysLeft <= 3;
            return (
              <div
                key={e.id}
                className={`flex items-center gap-3 p-3 rounded-xl border ${
                  isVeryUrgent
                    ? 'bg-red-50 border-red-200'
                    : isUrgent
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-indigo-50 border-indigo-200'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isVeryUrgent ? 'bg-red-100' : isUrgent ? 'bg-amber-100' : 'bg-indigo-100'
                }`}>
                  <i className={`fas fa-user text-sm ${
                    isVeryUrgent ? 'text-red-500' : isUrgent ? 'text-amber-500' : 'text-indigo-500'
                  }`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${
                    isVeryUrgent ? 'text-red-800' : isUrgent ? 'text-amber-800' : 'text-indigo-800'
                  }`}>{e.name}</p>
                  <p className={`text-xs truncate ${
                    isVeryUrgent ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-indigo-600'
                  }`}>
                    {e.project_name || '案件名未設定'} &nbsp;·&nbsp; {e.planner || '担当未設定'}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    isVeryUrgent
                      ? 'bg-red-200 text-red-700'
                      : isUrgent
                      ? 'bg-amber-200 text-amber-700'
                      : 'bg-indigo-200 text-indigo-700'
                  }`}>
                    {daysLeft <= 0 ? '本日終了' : `残${daysLeft}営業日`}
                  </span>
                  <p className={`text-[10px] mt-0.5 ${
                    isVeryUrgent ? 'text-red-400' : isUrgent ? 'text-amber-400' : 'text-indigo-400'
                  }`}>
                    {e.project_end_date.replace(/-/g, '/')} 終了
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {endingThisMonth.length > 0 && (
        <div className="mt-3 pt-3 border-t border-prodia-border">
          <p className="text-xs text-prodia-text-secondary flex items-center gap-1">
            <i className="fas fa-info-circle text-prodia-text-muted"></i>
            合計 <span className="font-bold text-prodia-primary">{endingThisMonth.length}名</span> のアサイン先確保が必要です
          </p>
        </div>
      )}
    </Card>
  );
};

// 今日のアクションカード
const TodayActionsCard = ({ interviews }) => {
  const today = new Date().toISOString().split('T')[0];
  const todayInterviews = interviews.filter(i => i.date === today);
  const urgentDeadlines = interviews.filter(i => i.deadline === today);

  return (
    <Card className="flex flex-col h-full">
      <h3 className="text-lg font-semibold text-prodia-text-primary mb-4 flex items-center gap-2">
        <i className="fas fa-exclamation-circle text-danger"></i>
        今日のアクション
      </h3>
      
      <div className="space-y-3">
        {urgentDeadlines.length > 0 && (
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl">
            <i className="fas fa-exclamation-triangle text-red-500 mt-1"></i>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">回答期限が今日</p>
              <p className="text-xs text-red-600">
                {urgentDeadlines.map(i => `${i.engineer}さん(${i.company}) ${i.time}まで`).join(', ')}
              </p>
            </div>
          </div>
        )}

        {todayInterviews.length > 0 && (
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <i className="fas fa-calendar-day text-blue-500 mt-1"></i>
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-800">本日の面談: {todayInterviews.length}件</p>
              <p className="text-xs text-blue-600">
                {todayInterviews.map(i => `${i.engineer}(${i.time})`).join(', ')}
              </p>
            </div>
          </div>
        )}

        {urgentDeadlines.length === 0 && todayInterviews.length === 0 && (
          <div className="text-center py-4 text-prodia-text-muted">
            <i className="fas fa-check-circle text-3xl mb-2 text-success"></i>
            <p className="text-sm">緊急アクションはありません</p>
          </div>
        )}
      </div>
    </Card>
  );
};

// 個人別決定ランキング
const DecisionRankingSection = ({ decisions, planners, onDetailClick, onOpenDetailAnalytics, ppGoal, bpGoal }) => {
  
  const plannerStats = useMemo(() => {
    const stats = {};
    planners.forEach(p => {
      stats[p] = { pp: 0, bp: 0, total: 0, ppDetails: [], bpDetails: [] };
    });
    
    decisions.forEach(d => {
      if (stats[d.planner]) {
        if (d.type === 'PP') {
          stats[d.planner].pp++;
          stats[d.planner].ppDetails.push(d);
        } else {
          stats[d.planner].bp++;
          stats[d.planner].bpDetails.push(d);
        }
        stats[d.planner].total++;
      }
    });
    
    return stats;
  }, [decisions, planners]);

  const getMedalIcon = (index) => {
    if (index === 0) return <i className="fas fa-trophy text-yellow-500"></i>;
    if (index === 1) return <i className="fas fa-medal text-gray-400"></i>;
    if (index === 2) return <i className="fas fa-award text-orange-600"></i>;
    return <i className="far fa-circle text-slate-300"></i>;
  };

  const sortedPP = planners.map(p => ({ name: p, count: plannerStats[p]?.pp ?? 0, details: plannerStats[p]?.ppDetails ?? [] })).sort((a, b) => b.count - a.count);
  const sortedBP = planners.map(p => ({ name: p, count: plannerStats[p]?.bp ?? 0, details: plannerStats[p]?.bpDetails ?? [] })).sort((a, b) => b.count - a.count);
  const sortedTotal = planners.map(p => ({ name: p, count: plannerStats[p]?.total ?? 0 })).sort((a, b) => b.count - a.count);

  return (
    <Card className="mb-6">
      <CardHeader
        title="個人別決定状況（今月）"
        action={
          <Button size="sm" onClick={onOpenDetailAnalytics}>
            <i className="fas fa-chart-line"></i>
            詳細分析を見る
          </Button>
        }
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* PP決定 */}
        <div className="border border-prodia-border rounded-xl p-4 bg-prodia-background">
          <h4 className="text-sm font-semibold text-prodia-text-primary mb-3 flex items-center justify-between">
            <span>PP決定 ({sortedPP.reduce((sum, p) => sum + p.count, 0)}/{ppGoal ?? 5}名)</span>
          </h4>
          <div className="space-y-2">
            {sortedPP.map((p, idx) => (
              <div 
                key={p.name} 
                className={`flex items-center justify-between p-2 rounded-lg ${p.count > 0 ? 'bg-prodia-surface cursor-pointer hover:bg-prodia-primary-subtle/40 border border-prodia-border' : 'bg-prodia-background'}`}
                onClick={() => p.count > 0 && onDetailClick && onDetailClick(p.name, 'PP', p.details)}
              >
                <div className="flex items-center gap-2">
                  <span>{getMedalIcon(idx)}</span>
                  <span className={`text-sm ${p.count > 0 ? 'font-semibold text-prodia-text-primary' : 'text-prodia-text-muted'}`}>
                    {p.name}
                  </span>
                </div>
                <span className={`text-sm font-bold ${p.count > 0 ? 'text-info' : 'text-prodia-text-muted'}`}>
                  {p.count}件
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* BP決定 */}
        <div className="border border-prodia-border rounded-xl p-4 bg-prodia-background">
          <h4 className="text-sm font-semibold text-prodia-text-primary mb-3 flex items-center justify-between">
            <span>BP決定 ({sortedBP.reduce((sum, p) => sum + p.count, 0)}/10名)</span>
          </h4>
          <div className="space-y-2">
            {sortedBP.map((p, idx) => (
              <div 
                key={p.name} 
                className={`flex items-center justify-between p-2 rounded-lg ${p.count > 0 ? 'bg-prodia-surface cursor-pointer hover:bg-success-bg/40 border border-prodia-border' : 'bg-prodia-background'}`}
                onClick={() => p.count > 0 && onDetailClick && onDetailClick(p.name, 'BP', p.details)}
              >
                <div className="flex items-center gap-2">
                  <span>{getMedalIcon(idx)}</span>
                  <span className={`text-sm ${p.count > 0 ? 'font-semibold text-prodia-text-primary' : 'text-prodia-text-muted'}`}>
                    {p.name}
                  </span>
                </div>
                <span className={`text-sm font-bold ${p.count > 0 ? 'text-success' : 'text-prodia-text-muted'}`}>
                  {p.count}件
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 合計 */}
        <div className="border border-prodia-border rounded-xl p-4 bg-prodia-background">
          <h4 className="text-sm font-semibold text-prodia-text-primary mb-3 flex items-center justify-between">
            <span>合計決定</span>
          </h4>
          <div className="space-y-2">
            {sortedTotal.map((p, idx) => (
              <div 
                key={p.name} 
                className={`flex items-center justify-between p-2 rounded-lg ${p.count > 0 ? 'bg-prodia-surface border border-prodia-border' : 'bg-prodia-background'}`}
              >
                <div className="flex items-center gap-2">
                  <span>{getMedalIcon(idx)}</span>
                  <span className={`text-sm ${p.count > 0 ? 'font-semibold text-prodia-text-primary' : 'text-prodia-text-muted'}`}>
                    {p.name}
                  </span>
                </div>
                <span className={`text-sm font-bold ${p.count > 0 ? 'text-prodia-primary' : 'text-prodia-text-muted'}`}>
                  {p.count}件
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

const EngineerStats = ({ engineers }) => {
  const { allMembers } = useSalesMembers();
  const planners = useMemo(
    () => allMembers.map((member) => member.display_name),
    [allMembers],
  );
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [isDetailAnalyticsOpen, setIsDetailAnalyticsOpen] = useState(false);

  // 月済目標（localStoragラ毎月保存）
  const [monthlyGoals, setMonthlyGoals] = useState(() => {
    try {
      const saved = localStorage.getItem('monthlyGoals');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const getPpGoal = (month) => monthlyGoals[month]?.pp ?? 5;
  const getBpGoal = (month) => monthlyGoals[month]?.bp ?? 10;

  const updateGoal = (month, type, value) => {
    const num = Math.max(0, parseInt(value) || 0);
    setMonthlyGoals(prev => {
      const next = { ...prev, [month]: { ...(prev[month] || {}), [type]: num } };
      try { localStorage.setItem('monthlyGoals', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // ダッシュボードで参照する派生データをまとめて計算
  const stats = useMemo(() => {
    const total = engineers.length;
    const assigned = engineers.filter(e => e.engineer_status === 'アサイン済').length;
    const unassigned = engineers.filter(e => e.engineer_status === '未アサイン').length;

    const skillCounts = {};
    const plannerCounts = {};
    const statusCounts = {};
    const phaseCounts = {};
    const benchSkillCounts = {};
    let totalSkillEntries = 0;

    engineers.forEach(engineer => {
      const status = engineer.engineer_status || '未設定';
      statusCounts[status] = (statusCounts[status] || 0) + 1;

      const planner = engineer.planner?.trim() || '未割り当て';
      plannerCounts[planner] = (plannerCounts[planner] || 0) + 1;

      const phases = Array.isArray(engineer.phase) && engineer.phase.length > 0 ? engineer.phase : ['未設定'];
      phases.forEach(phase => {
        phaseCounts[phase] = (phaseCounts[phase] || 0) + 1;
      });

      if (Array.isArray(engineer.skills)) {
        engineer.skills.forEach(skill => {
          skillCounts[skill] = (skillCounts[skill] || 0) + 1;
          totalSkillEntries += 1;

          if (engineer.engineer_status === '未アサイン') {
            benchSkillCounts[skill] = (benchSkillCounts[skill] || 0) + 1;
          }
        });
      }
    });

    const topSkills = Object.entries(skillCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    const benchHotSkills = Object.entries(benchSkillCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);

    const plannerRanking = Object.entries(plannerCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);

    return {
      total,
      assigned,
      unassigned,
      assignmentRate: total > 0 ? Math.round((assigned / total) * 100) : 0,
      averageSkills: total > 0 ? (totalSkillEntries / total).toFixed(1) : '0.0',
      uniqueSkills: Object.keys(skillCounts).length,
      uniquePlanners: Object.keys(plannerCounts).filter(name => name !== '未割り当て').length,
      topSkills,
      benchHotSkills,
      plannerRanking,
      statusCounts,
      phaseCounts
    };
  }, [engineers]);

  const handleDetailClick = (planner, type, details) => {
    setModalData({ planner, type, details });
    setShowDetailModal(true);
  };

  // PP営業進捗から実際のデータを取得
  const [ppInterviews, setPpInterviews] = React.useState([]);
  
  React.useEffect(() => {
    const loadPPInterviews = () => {
      apiFetch('/pp-interviews/')
        .then(res => res.json())
        .then(data => setPpInterviews(Array.isArray(data) ? data : (data.results || [])))
        .catch(e => console.error('PP営業データの読み込みに失敗しました', e));
    };
    
    // 初回読み込み
    loadPPInterviews();
    
    // 定期的にデータを更新（30秒ごと）
    const interval = setInterval(loadPPInterviews, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // BP進捗からリアルデータを取得
  const [bpProspects, setBpProspects] = React.useState([]);

  React.useEffect(() => {
    const loadBPProspects = () => {
      apiFetch('/bp-prospects/')
        .then(res => res.json())
        .then(data => setBpProspects(Array.isArray(data) ? data : (data.results || [])))
        .catch(e => console.error('BP見込みデータの読み込みに失敗しました', e));
    };
    loadBPProspects();
    const interval = setInterval(loadBPProspects, 30000);
    return () => clearInterval(interval);
  }, []);

  // 成約になった月を履歴から取得する（"YYYY-MM" 形式）
  // history に「ステータス → 成約」の変更が記録されている場合はその月、
  // 見つからない場合は created_at の年月にフォールバック
  const getWonMonth = (item) => {
    if (Array.isArray(item.history)) {
      const wonEntry = [...item.history].reverse().find(h =>
        Array.isArray(h.changes) && h.changes.some(c => c.new === '成約')
      );
      if (wonEntry?.timestamp) {
        return wonEntry.timestamp.substring(0, 7); // "YYYY-MM"
      }
    }
    // フォールバック: created_at（"YYYY-MM-DDT..." 形式）
    if (item.created_at) return item.created_at.substring(0, 7);
    return null;
  };

  // 選択月のデータ（PP・BP実データから統合）
  // ※ 成約になった月（history の timestamp）で集計する
  const currentMonthDecisions = React.useMemo(() => {
    const ppWon = ppInterviews
      .filter(i => i.status === '成約' && getWonMonth(i) === selectedMonth)
      .map(i => ({ ...i, planner: i.sales_person, type: 'PP' }));
    const bpWon = bpProspects
      .filter(p => p.status === '成約' && getWonMonth(p) === selectedMonth)
      .map(p => ({ ...p, planner: p.main_planner, type: 'BP' }));
    return [...ppWon, ...bpWon];
  }, [ppInterviews, bpProspects, selectedMonth]);

  // 営業データ統計 - 成約になった月（history）で集計
  const currentMonthStr = selectedMonth;
  const ppDecisions = ppInterviews.filter(i =>
    i.status === '成約' && getWonMonth(i) === currentMonthStr
  ).length;

  // BP進捗の実データ（成約になった月で集計）
  const bpDecisions = bpProspects.filter(p =>
    p.status === '成約' && getWonMonth(p) === currentMonthStr
  ).length;

  const inProgressCount = ppInterviews.filter(i =>
    ['日程調整中', '面談予定', '面談済み', '回答待ち'].includes(i.status) &&
    i.start_month === currentMonthStr
  ).length;

  // 前月データ - PP/BP両方の実データを使用
  const prevMonthStr = (() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const prev = new Date(y, m - 2, 1); // m-1が現在月(0-indexed)なのでm-2が前月
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  })();
  const ppPrevMonth = ppInterviews.filter(i =>
    i.status === '成約' && getWonMonth(i) === prevMonthStr
  ).length;
  const bpPrevMonth = bpProspects.filter(p =>
    p.status === '成約' && getWonMonth(p) === prevMonthStr
  ).length;
  
  // 前月比
  const ppChange = ppDecisions - ppPrevMonth;
  const bpChange = bpDecisions - bpPrevMonth;
  
  // 成約率計算 - PP営業進捗の実データを使用
  const totalInterviews = ppInterviews.filter(i => 
    i.start_month === currentMonthStr && 
    !['失注'].includes(i.status)
  ).length;
  const totalDecisions = ppDecisions + bpDecisions;
  const successRate = totalInterviews > 0 ? Math.round((ppDecisions / totalInterviews) * 100) : 0;

  return (
    <div className="bg-prodia-background min-h-screen">
      <PageHeader
        title="エンジニア統計"
        description="決定目標・KPI・リソース分析の司令塔ダッシュボード"
      />
      <div className="max-w-[1800px] mx-auto p-6">
        {/* 🎯 ZONE 1: 司令塔 */}
        <MonthlyGoalBanner 
          ppGoal={getPpGoal(selectedMonth)} 
          bpGoal={getBpGoal(selectedMonth)} 
          ppAchieved={ppDecisions} 
          bpAchieved={bpDecisions}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          ppPrevMonth={ppPrevMonth}
          bpPrevMonth={bpPrevMonth}
          onPpGoalChange={(v) => updateGoal(selectedMonth, 'pp', v)}
          onBpGoalChange={(v) => updateGoal(selectedMonth, 'bp', v)}
        />

        {/* アクションエリア：今月 / 今日 横並び */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <MonthlyActionsCard engineers={engineers} />
          <TodayActionsCard
            interviews={ppInterviews.map(i => ({
              ...i,
              engineer: i.engineer_name,
              company: i.company_name,
              date: i.interview_date,
              time: i.interview_time,
              deadline: i.response_deadline,
              type: 'PP'
            }))}
          />
        </div>

        {/* 📊 ZONE 2: 戦況（KPI） */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-prodia-text-primary mb-4 flex items-center gap-2">
            <i className="fas fa-chart-line text-prodia-text-muted"></i>
            KPIダッシュボード
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KpiCard 
              icon="fas fa-check-circle" 
              label="PP決定" 
              value={ppDecisions} 
              sub={ppChange !== 0 ? `今月の実績（前月比 ${ppChange > 0 ? '+' : ''}${ppChange}）` : '今月の実績'}
              suffix="件"
              accent="info"
            />
            <KpiCard 
              icon="fas fa-handshake" 
              label="BP決定" 
              value={bpDecisions} 
              sub={bpChange !== 0 ? `今月の実績（前月比 ${bpChange > 0 ? '+' : ''}${bpChange}）` : '今月の実績'}
              suffix="件"
              accent="success"
            />
            <KpiCard 
              icon="fas fa-tasks" 
              label="進行中" 
              value={inProgressCount} 
              sub="面談・回答待ち"
              suffix="件"
              accent="warning"
            />
            <KpiCard 
              icon="fas fa-percentage" 
              label="成約率" 
              value={successRate} 
              sub={`${totalDecisions}/${totalInterviews}件`}
              suffix="%"
              accent="primary"
            />
            <KpiCard 
              icon="fas fa-chart-pie" 
              label="稼働率" 
              value={stats.assignmentRate} 
              sub={`${stats.assigned}/${stats.total}名`}
              suffix="%"
              accent="primary"
            />
          </div>
        </div>

        <DecisionRankingSection 
          decisions={currentMonthDecisions}
          planners={planners}
          onDetailClick={handleDetailClick}
          onOpenDetailAnalytics={() => setIsDetailAnalyticsOpen(true)}
          ppGoal={getPpGoal(selectedMonth)}
          bpGoal={getBpGoal(selectedMonth)}
        />

        {/* ⚙️ ZONE 3: 戦力（リソース分析） */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* スキルインサイト */}
          <Card>
            <CardHeader title="スキルセット分布" />
            <div className="space-y-3">
              {stats.topSkills.map(([skill, count], index) => (
                <div key={skill}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-prodia-primary">#{index + 1}</span>
                      <span className="text-sm font-semibold text-prodia-text-primary">{skill}</span>
                    </div>
                    <span className="text-xs font-bold text-prodia-text-secondary">{count}名</span>
                  </div>
                  <div className="h-2 bg-prodia-background rounded-full overflow-hidden">
                    <div
                      className="h-full bg-prodia-primary"
                      style={{ width: stats.total > 0 ? `${(count / stats.total) * 100}%` : '0%' }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="bg-prodia-background rounded-xl p-3 text-center border border-prodia-border">
                <p className="text-xs text-prodia-text-secondary mb-1">平均スキル数</p>
                <p className="text-2xl font-semibold text-prodia-text-primary">{stats.averageSkills}</p>
              </div>
              <div className="bg-prodia-background rounded-xl p-3 text-center border border-prodia-border">
                <p className="text-xs text-prodia-text-secondary mb-1">スキル領域</p>
                <p className="text-2xl font-semibold text-prodia-text-primary">{stats.uniqueSkills}</p>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="プランナー別担当数" />
            <div className="space-y-3">
              {stats.plannerRanking.map((planner) => (
                <div key={planner.name} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-prodia-primary-subtle flex items-center justify-center text-prodia-primary font-bold text-sm">
                    {planner.name.slice(0, 2)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-prodia-text-primary">{planner.name}</span>
                      <span className="text-sm font-bold text-prodia-primary">{planner.count}名</span>
                    </div>
                    <div className="h-2 bg-prodia-background rounded-full overflow-hidden">
                      <div
                        className="h-full bg-prodia-primary"
                        style={{ width: stats.total > 0 ? `${(planner.count / stats.total) * 100}%` : '0%' }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* 詳細モーダル */}
        {showDetailModal && modalData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
            <div className="bg-prodia-surface rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-auto border border-prodia-border" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-prodia-surface border-b border-prodia-border text-prodia-text-primary p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold">
                    {modalData.planner}さんの{modalData.type}決定詳細
                  </h3>
                  <button 
                    onClick={() => setShowDetailModal(false)}
                    className="w-8 h-8 rounded-lg bg-prodia-background hover:bg-prodia-primary-subtle flex items-center justify-center transition-colors text-prodia-text-secondary"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm text-prodia-text-secondary">決定数</p>
                  <p className="text-3xl font-semibold text-prodia-text-primary">{modalData.details.length}件</p>
                </div>

                <div className="space-y-3">
                  {modalData.details.map(d => {
                    const isBP = modalData.type === 'BP';
                    const grossProfit = isBP && d.sales_price && d.purchase_price ? d.sales_price - d.purchase_price : null;
                    return (
                    <div key={d.id} className="border border-prodia-border rounded-xl p-4 hover:shadow-prodia-md transition-all bg-prodia-surface">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-prodia-text-primary">{d.engineer_name}</h4>
                        <span className="text-sm font-semibold text-success">決定</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-xs text-prodia-text-muted">企業名</p>
                          <p className="text-prodia-text-primary">{d.company_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-prodia-text-muted">決定日</p>
                          <p className="text-prodia-text-primary">{d.decision_date}</p>
                        </div>
                        <div>
                          <p className="text-xs text-prodia-text-muted">開始月</p>
                          <p className="text-prodia-text-primary">{d.start_month}</p>
                        </div>
                        {isBP ? (
                          <>
                            <div>
                              <p className="text-xs text-slate-500">売上単価</p>
                              <p className="text-blue-700 font-bold">{d.sales_price ? d.sales_price.toLocaleString() + '円' : '未登録'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">仕入れ単価</p>
                              <p className="text-orange-700 font-bold">{d.purchase_price ? d.purchase_price.toLocaleString() + '円' : '未登録'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">粗利</p>
                              <p className={`font-bold ${grossProfit !== null && grossProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                                {grossProfit !== null ? grossProfit.toLocaleString() + '円' : '未計算'}
                              </p>
                            </div>
                          </>
                        ) : (
                          <div>
                            <p className="text-xs text-slate-500">単価</p>
                            <p className="text-slate-700 font-bold">{d.unit_price.toLocaleString()}円</p>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 詳細分析モーダル */}
      <DetailedAnalytics
        isOpen={isDetailAnalyticsOpen}
        onClose={() => setIsDetailAnalyticsOpen(false)}
        decisionsData={DECISIONS_BY_MONTH}
        availableMonths={Object.keys(DECISIONS_BY_MONTH).sort().reverse()}
      />
    </div>
  );
};

export default EngineerStats;
